import { DynamicPropertyEvent } from 'types/DynamicPropertyEvent';

import DynamicGroup from './Types/Group';

interface MergeOptions {
    addMissing?: boolean;
}

/**
 * Merges `incomingGroup`'s children into `targetGroup` by name. Where both
 * sides have a group of the same name, it recurses; otherwise a matching
 * child takes the incoming value. An incoming child with no match is added,
 * unless `addMissing` is false.
 */
export function merge(
    targetGroup: DynamicGroup,
    incomingGroup: DynamicGroup,
    options?: MergeOptions
) {
    const { addMissing = true } = options ?? {};
    for (const incomingProperty of incomingGroup.properties) {
        const targetProperty = targetGroup.getChild(incomingProperty.name);
        if (targetProperty) {
            if (
                incomingProperty instanceof DynamicGroup &&
                targetProperty instanceof DynamicGroup
            ) {
                merge(targetProperty, incomingProperty, {
                    addMissing,
                });
            } else {
                targetProperty.set(incomingProperty.value);
            }
        } else if (addMissing) {
            targetGroup.addProperty(incomingProperty);
        }
    }
    // No ADDED here: addProperty dispatches its own for each child it adds,
    // and dispatching one regardless also fired it for merges that added
    // nothing - e.g. a clipboard paste, which never adds - and pasting into the
    // creative's top-level properties scrolled the properties drawer to the
    // bottom.
    //
    // CHANGED, on the other hand, is unconditional and last. An overwritten
    // child passes its CHANGED up to this group only if setState created it,
    // and the same goes for a nested group merged into above. So even when
    // addProperty has already dispatched CHANGED, a later overwrite may not
    // have been heard, and this is what tells listeners on this group.
    targetGroup.dispatch(DynamicPropertyEvent.CHANGED);
}
